import { getDownloadURL, ref, StorageReference } from 'firebase/storage';
import { storage } from '@/firebase/firebase';

// Signed URL cache — avoids redundant getDownloadURL network calls when
// components remount (e.g. navigating back to a note page).
const SIGNED_URL_TTL_MS = 30 * 60 * 1000; // 30 minutes

interface CachedUrl {
  url: string;
  expiresAt: number;
}

const signedUrlCache = new Map<string, CachedUrl>();

const getCachedUrl = (fullPath: string): string | undefined => {
  const entry = signedUrlCache.get(fullPath);
  if (!entry) return undefined;
  if (Date.now() >= entry.expiresAt) {
    signedUrlCache.delete(fullPath);
    return undefined;
  }
  return entry.url;
};

/**
 * Get download URL while suppressing 404 console errors
 * Firebase automatically logs 404s to console even when caught
 * Results are cached in memory for 30 minutes to avoid redundant network calls.
 */
export const getDownloadURLSilently = async (fileRef: StorageReference): Promise<string> => {
  const cached = getCachedUrl(fileRef.fullPath);
  if (cached) return cached;

  // Store original console.error
  const originalError = console.error;

  try {
    // Temporarily suppress console.error for Firebase 404s
    console.error = (...args: unknown[]) => {
      const message = args[0]?.toString() || '';
      // Only suppress Firebase storage 404 errors
      if (message.includes('404') && message.includes('firebasestorage.googleapis.com')) {
        return; // Suppress this error
      }
      // Allow other errors through
      originalError.apply(console, args);
    };

    const url = await getDownloadURL(fileRef);
    signedUrlCache.set(fileRef.fullPath, { url, expiresAt: Date.now() + SIGNED_URL_TTL_MS });
    return url;
  } finally {
    // Always restore original console.error
    console.error = originalError;
  }
};

/**
 * Resolve a media URL field from the API to a playable URL.
 *
 * The backend returns one of two formats during the Cloud CDN rollout:
 *   - `gs://<bucket>/<path>` — legacy, must be resolved via the Firebase
 *     Storage SDK to get a tokenized firebasestorage.googleapis.com URL.
 *   - `https://cdn-staging.notego.ai/...?Expires=...&Signature=...` — already
 *     a pre-signed Cloud CDN URL. Use as-is; MUST NOT be modified (any edit
 *     invalidates the signature and the CDN will return 403).
 *
 * Signed CDN URLs expire ~24h after the API served them. Callers must NOT
 * persist the resolved URL to localStorage / IndexedDB — refetch the note
 * from the API to get a fresh URL. In-memory React/Zustand state is fine.
 */
export const resolveMediaUrl = async (raw: string | null | undefined): Promise<string | null> => {
  if (!raw) return null;
  if (typeof raw !== 'string') return raw;
  if (!raw.startsWith('gs://')) return raw;
  return getDownloadURLSilently(ref(storage, raw));
};
